/**
 * Asset type utilities for consistent icons, display names, and ordering across the application
 */

import { BUNNY_STREAM_STATUS } from './bunnyConstants';

/**
 * Asset type definitions with all associated metadata
 */
export const ASSET_TYPES = {
  videos: {
    key: 'videos',
    displayName: 'Videos',
    icon: 'clapperboard-play',
    order: 1
  },
  selects: {
    key: 'selects',
    displayName: 'Video Clips',
    icon: 'film',
    order: 2
  },
  social:{
    key: 'social',
    displayName: 'Social Videos',
    icon: 'mobile-screen',
    order: 3
  },
  photography: {
    key: 'photography',
    displayName: 'Photos',
    icon: 'image',
    order: 4
  },
  graphics: {
    key: 'graphics',
    displayName: 'Graphics',
    icon: 'pen-nib',
    order: 5
  },
  audio: {
    key: 'audio',
    displayName: 'Audio',
    icon: 'microphone',
    order: 6
  },
  documents: {
    key: 'documents',
    displayName: 'Documents',
    icon: 'file-lines',
    order: 7
  },
  'brand assets': {
    key: 'brand assets',
    displayName: 'Brand Assets',
    icon: 'palette',
    order: 8
  }
};

/**
 * Orientation values for aspect ratio classification
 */
const ORIENTATIONS = ['horizontal', 'vertical', 'square'];

/**
 * Get the preferred order of asset types for display
 * @returns {string[]} Array of asset type keys in preferred order
 */
export const getAssetTypeOrder = () => {
  return Object.values(ASSET_TYPES)
    .sort((a, b) => a.order - b.order)
    .map(type => type.key);
};

/**
 * Get the sort priority for an asset type (lower = higher priority)
 * Uses the order field from ASSET_TYPES, with a high default for unknown types
 * @param {string} assetType - The type of asset
 * @returns {number} Sort priority
 */
export const getAssetTypePriority = (assetType) => {
  const normalizedType = assetType?.toLowerCase();
  return ASSET_TYPES[normalizedType]?.order ?? 99;
};

/**
 * Get the FontAwesome icon name for an asset type
 * @param {string} assetType - The type of asset
 * @returns {string} FontAwesome icon name
 */
export const getAssetTypeIcon = (assetType) => {
  const normalizedType = assetType?.toLowerCase();
  return ASSET_TYPES[normalizedType]?.icon || 'file';
};

/**
 * Get the display name for an asset type
 * @param {string} assetType - The type of asset
 * @returns {string} Human-readable display name
 */
export const getAssetTypeDisplayName = (assetType) => {
  const normalizedType = assetType?.toLowerCase();
  return ASSET_TYPES[normalizedType]?.displayName || 
    (assetType?.charAt(0).toUpperCase() + assetType?.slice(1)) || 'Unknown';
};

/**
 * Get all available asset types that exist in the provided asset collection
 * @param {Array} assets - Array of asset objects
 * @returns {string[]} Array of asset type keys that exist in the collection, in preferred order
 */
export const getAvailableAssetTypes = (assets) => {
  const existingTypes = new Set();
  
  assets.forEach((asset) => {
    if (asset.assetType) {
      existingTypes.add(asset.assetType.toLowerCase());
    }
  });

  return getAssetTypeOrder().filter(type => existingTypes.has(type));
};

/**
 * Determine orientation from various input formats.
 * Single source of truth for orientation detection.
 *
 * Accepts:
 *  - Orientation string pass-through: "horizontal", "vertical", "square"
 *  - Two args (width, height): raw pixel dimensions
 *  - "W:H" string (legacy): e.g. "16:9", "1080:1920"
 *  - Decimal number: e.g. 1.778 (16/9)
 *
 * @param {string|number} widthOrValue - Width, orientation string, "W:H" string, or decimal ratio
 * @param {number} [height] - Height (when first arg is width)
 * @returns {string|null} "horizontal" | "vertical" | "square" | null
 */
export const getOrientation = (widthOrValue, height) => {
  // Pass-through: already an orientation string
  if (ORIENTATIONS.includes(widthOrValue)) return widthOrValue;

  // Two args: raw width/height
  if (height !== undefined) {
    const w = Number(widthOrValue), h = Number(height);
    if (!w || !h) return null;
    if (Math.abs(w - h) / Math.max(w, h) < 0.05) return 'square';
    return w > h ? 'horizontal' : 'vertical';
  }

  // Single arg: "W:H" string (legacy)
  if (typeof widthOrValue === 'string' && widthOrValue.includes(':')) {
    const [w, h] = widthOrValue.split(':').map(Number);
    if (w && h) return getOrientation(w, h);
    return null;
  }

  // Decimal ratio
  const ratio = Number(widthOrValue);
  if (!ratio || isNaN(ratio)) return null;
  if (Math.abs(ratio - 1) < 0.05) return 'square';
  return ratio > 1 ? 'horizontal' : 'vertical';
};

/**
 * Returns true if the asset originated from a client self-serve library upload.
 * Provenance is determined by uploadSource (NOT the uploadedByClient boolean,
 * which is unreliable — review approvals hardcode it to true).
 * @param {Object} asset - Asset document
 * @returns {boolean}
 */
export const isClientStoreAsset = (asset) =>
  asset?.uploadSource === 'client-store';

/**
 * Returns true if the asset originated from a client transfer upload.
 * @param {Object} asset - Asset document
 * @returns {boolean}
 */
const isClientTransferAsset = (asset) =>
  asset?.uploadSource === 'client-transfer';

/**
 * Filter an asset list for client-facing pickers.
 * - excludeTransfer: drop client-transfer assets (always on in client flows —
 *   transfers should never appear as library assets)
 * - restrictClientStore: also drop client-store assets (contract gate active —
 *   company lacks the includeClientMediaEdits feature)
 * No-op when both flags are false (admin pickers pass nothing).
 * Legacy assets with a missing or 'admin' uploadSource are always kept.
 * @param {Array} assets - Asset list to filter
 * @param {Object} [options]
 * @param {boolean} [options.excludeTransfer=false]
 * @param {boolean} [options.restrictClientStore=false]
 * @returns {Array} Filtered asset list
 */
export const applyClientAssetRestrictions = (
  assets,
  { excludeTransfer = false, restrictClientStore = false } = {}
) => {
  if (!Array.isArray(assets)) return [];
  if (!excludeTransfer && !restrictClientStore) return assets;
  return assets.filter((asset) => {
    if (excludeTransfer && isClientTransferAsset(asset)) return false;
    if (restrictClientStore && isClientStoreAsset(asset)) return false;
    return true;
  });
};

export const CLIENT_MEDIA_RESTRICTION_NOTICE =
  'Use of Non-Anthem Related assets will be reviewed';

/**
 * Prepare asset update data based on user permissions and form data
 * @param {Object} formData - Form data from component
 * @param {Object} asset - Current asset data
 * @param {boolean} isAdmin - Is user admin/super
 * @param {Array} availableProjects - Available projects for selection
 * @returns {Object} - Prepared update data
 */
export const prepareUpdateData = (formData, asset, isAdmin = false, availableProjects = []) => {
  const updateData = {
    name: formData.name?.trim() || asset.name,
    tags: Array.isArray(formData.tags)
      ? formData.tags.filter(tag => tag?.trim())
      : [],
    description: formData.description?.trim() || ''
  };

  // Add transcript for video assets. A word-level transcript is owned by the
  // transcription pipeline (rendered VTT in `transcript`, raw words in
  // assetTranscripts/{id}) — never overwrite it from the manual edit form, or a
  // save would clobber the VTT and orphan the words doc. The drawer keeps the
  // textarea read-only in that case (see AssetDetail).
  if ((asset.assetType === 'videos' || asset.assetType === 'selects' || asset.assetType === 'audio') && asset.transcriptWordLevel !== true) {
    // Always include transcript field (even if empty) to allow clearing existing transcripts
    updateData.transcript = formData.transcript?.trim() || '';
  }

  // Handle bucket field - available for all users
  // Empty string or null means remove bucket assignment
  if (formData.bucket !== undefined) {
    updateData.bucket = formData.bucket?.trim() || null;
  }

  // Add admin-only fields
  if (isAdmin) {
    updateData.assetType = formData.assetType || asset.assetType;
    updateData.company = formData.company || asset.company;
    updateData.project = formData.project || asset.project;

    // Add project name for display
    if (formData.project && availableProjects.length > 0) {
      const selectedProject = availableProjects.find(
        p => p.id === formData.project
      );
      updateData.projectName = selectedProject?.name || '';
    }
  }

  return updateData;
};

/**
 * File type icon mappings for displaying icons when thumbnails aren't available
 * Maps MIME types and file extensions to FontAwesome icons
 */
const FILE_TYPE_ICONS = {
  // Video types
  'video/mp4': 'video',
  'video/quicktime': 'video',
  'video/webm': 'video',
  'video/x-msvideo': 'video',
  'video/x-matroska': 'video',
  'video/': 'video', // Generic video prefix

  // Audio types
  'audio/mpeg': 'waveform-lines',
  'audio/mp3': 'waveform-lines',
  'audio/wav': 'waveform-lines',
  'audio/x-wav': 'waveform-lines',
  'audio/aac': 'waveform-lines',
  'audio/ogg': 'waveform-lines',
  'audio/flac': 'waveform-lines',
  'audio/': 'waveform-lines', // Generic audio prefix

  // Image types (fallback when no thumbnail)
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'image/svg+xml': 'file-svg',
  'image/': 'image', // Generic image prefix

  // Document types
  'application/pdf': 'file-pdf',
  'application/msword': 'file-word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-word',
  'application/vnd.ms-excel': 'file-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-excel',
  'application/vnd.ms-powerpoint': 'file-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'file-powerpoint',
  'text/plain': 'file-lines',
  'text/csv': 'file-csv',
  'application/json': 'file-code',
  'text/html': 'file-code',
  'text/css': 'file-code',
  'text/javascript': 'file-code',
  'application/javascript': 'file-code',

  // Archive types
  'application/zip': 'file-zipper',
  'application/x-rar-compressed': 'file-zipper',
  'application/x-7z-compressed': 'file-zipper',
  'application/gzip': 'file-zipper',
  'application/x-tar': 'file-zipper',

  // Design/Brand asset types
  'application/postscript': 'file-eps', // AI, EPS
  'application/eps': 'file-eps',
  'image/x-eps': 'file-eps',
  'application/illustrator': 'pen-nib',
  'image/vnd.adobe.photoshop': 'image-polaroid',
  'application/x-photoshop': 'image-polaroid',

  // Font types
  'font/ttf': 'font-case',
  'font/otf': 'font-case',
  'font/woff': 'font-case',
  'font/woff2': 'font-case',
  'application/x-font-ttf': 'font-case',
  'application/x-font-otf': 'font-case',
};

/**
 * File extension to icon mappings (fallback when MIME type isn't available)
 */
const FILE_EXTENSION_ICONS = {
  // Video
  mp4: 'video',
  mov: 'video',
  avi: 'video',
  mkv: 'video',
  webm: 'video',
  wmv: 'video',
  flv: 'video',
  m4v: 'video',

  // Audio
  mp3: 'waveform-lines',
  wav: 'waveform-lines',
  aac: 'waveform-lines',
  flac: 'waveform-lines',
  ogg: 'waveform-lines',
  m4a: 'waveform-lines',
  wma: 'waveform-lines',
  aiff: 'waveform-lines',

  // Images
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  bmp: 'image',
  tiff: 'image',
  tif: 'image',
  heic: 'image',
  heif: 'image',
  raw: 'image',
  cr2: 'image',
  nef: 'image',
  arw: 'image',
  dng: 'image',

  // Vector/Design
  svg: 'file-svg',
  ai: 'pen-nib',
  eps: 'file-eps',
  psd: 'image-polaroid',
  psb: 'image-polaroid',
  sketch: 'object-group',
  fig: 'object-group',
  xd: 'object-group',
  indd: 'newspaper',

  // Documents
  pdf: 'file-pdf',
  doc: 'file-word',
  docx: 'file-word',
  xls: 'file-excel',
  xlsx: 'file-excel',
  ppt: 'file-powerpoint',
  pptx: 'file-powerpoint',
  txt: 'file-lines',
  rtf: 'file-lines',
  csv: 'file-csv',

  // Code
  json: 'file-code',
  xml: 'file-code',
  html: 'file-code',
  css: 'file-code',
  js: 'file-code',
  ts: 'file-code',
  jsx: 'file-code',
  tsx: 'file-code',
  md: 'file-lines',

  // Archives
  zip: 'file-zipper',
  rar: 'file-zipper',
  '7z': 'file-zipper',
  tar: 'file-zipper',
  gz: 'file-zipper',

  // Fonts
  ttf: 'font-case',
  otf: 'font-case',
  woff: 'font-case',
  woff2: 'font-case',
  eot: 'font-case',
};

/**
 * Get the FontAwesome icon name for a file based on its MIME type, extension, or asset type
 * @param {Object} options - Options object
 * @param {string} [options.mimeType] - MIME type of the file (e.g., 'video/mp4')
 * @param {string} [options.fileName] - File name to extract extension from
 * @param {string} [options.assetType] - Asset type category (e.g., 'videos', 'audio')
 * @param {string} [options.iconStyle='fad'] - FontAwesome icon style prefix
 * @returns {Array} FontAwesome icon array [style, iconName]
 */
export const getFileTypeIcon = ({ mimeType, fileName, assetType, iconStyle = 'fad' } = {}) => {
  let iconName = 'file'; // Default fallback

  // First, try to match by MIME type
  if (mimeType) {
    const lowerMime = mimeType.toLowerCase();

    // Check exact match first
    if (FILE_TYPE_ICONS[lowerMime]) {
      iconName = FILE_TYPE_ICONS[lowerMime];
    } else {
      // Check prefix matches (e.g., 'video/', 'audio/', 'image/')
      for (const [prefix, icon] of Object.entries(FILE_TYPE_ICONS)) {
        if (prefix.endsWith('/') && lowerMime.startsWith(prefix)) {
          iconName = icon;
          break;
        }
      }
    }
  }

  // If still default, try file extension
  if (iconName === 'file' && fileName) {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension && FILE_EXTENSION_ICONS[extension]) {
      iconName = FILE_EXTENSION_ICONS[extension];
    }
  }

  // If still default, try asset type category
  if (iconName === 'file' && assetType) {
    const normalizedType = assetType.toLowerCase();
    switch (normalizedType) {
      case 'videos':
      case 'selects':
      case 'social':
        iconName = 'video';
        break;
      case 'audio':
        iconName = 'waveform-lines';
        break;
      case 'photography':
        iconName = 'image';
        break;
      case 'graphics':
        iconName = 'pen-nib';
        break;
      case 'documents':
        iconName = 'file-lines';
        break;
      case 'brand assets':
        iconName = 'palette';
        break;
    }
  }

  return [iconStyle, iconName];
};

/**
 * Get just the icon name (without style) for a file
 * Convenience wrapper around getFileTypeIcon
 * @param {Object} options - Same options as getFileTypeIcon
 * @returns {string} FontAwesome icon name
 */
export const getFileTypeIconName = (options) => {
  return getFileTypeIcon(options)[1];
};

/**
 * Format a byte count into a human-readable file size string.
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';

  // Decimal (SI) units — matches cloud-storage provider convention (Bunny,
  // AWS, GCP, Azure) so asset sizes line up with what the upstream reports.
  const k = 1000;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Re-export for backward compatibility with existing code
export const getAssetIconName = getAssetTypeIcon;

// Build a download filename for an asset. asset.name is the basename
// without extension (set at upload time); the extension is derived from
// whichever storage path is populated — bunnyStorage.path for Bunny
// assets, asset.file for legacy Firebase assets. Returns asset.name
// unchanged if neither path carries an extension (e.g. Bunny video).
export function buildAssetDownloadFilename(asset) {
  const name = asset?.name || '';
  if (!name || name.includes('.')) return name;
  const path = asset?.bunnyStorage?.path || asset?.file;
  if (!path) return name;
  const dot = path.lastIndexOf('.');
  if (dot < 0) return name;
  const ext = path.substring(dot);
  return ext.length > 1 ? name + ext : name;
}

/* -------------------------------------------------------------------------
 * Bunny predicates — single source of truth.
 *
 * These were previously hand-rolled inline in ~15 components with divergent
 * logic (3-pointer vs 4-pointer "is Bunny" checks, crossorigin keyed off the
 * unreliable status string, etc.). Centralized here so the rules can't drift.
 * ---------------------------------------------------------------------- */

/**
 * True if the asset has any Bunny pointer. Preferred over checking
 * storageBackend === 'bunny' alone because some older docs carry Bunny
 * fields without the flag (migration artifacts, early Bunny uploads).
 * Checks all four pointers — omitting bunnyThumbnail.path is the subtle
 * drift that caused inline copies to misclassify thumbnail-only assets.
 * @param {Object} asset
 * @returns {boolean}
 */
export function isBunnyBackedAsset(asset) {
  if (!asset) return false;
  return (
    asset.storageBackend === 'bunny' ||
    !!asset.bunnyStream?.videoId ||
    !!asset.bunnyStorage?.path ||
    !!asset.bunnyThumbnail?.path
  );
}

/**
 * True if a review version is Bunny-backed (versions don't carry
 * storageBackend, so this checks the stream/storage pointers).
 * @param {Object} version
 * @returns {boolean}
 */
export function isBunnyVersion(version) {
  return !!(version?.bunnyStream?.videoId || version?.bunnyStorage?.path);
}

/**
 * True if the asset has any renderable thumbnail source — a cached
 * thumbnail, a Bunny Stream thumbnail, or a Bunny Storage object that is
 * its own thumbnail (image/audio/doc).
 * @param {Object} asset
 * @returns {boolean}
 */
export function hasBunnyThumbnail(asset) {
  return (
    !!asset?.thumbnail ||
    !!asset?.bunnyStream?.thumbnailPath ||
    !!asset?.bunnyStorage?.path
  );
}

/**
 * Accepts an asset, a review version, or a raw bunnyStream object and
 * returns the bunnyStream sub-object (or the value itself if it already
 * looks like a stream). Internal helper for the status predicates.
 */
function resolveStream(streamLike) {
  if (!streamLike) return null;
  return streamLike.bunnyStream || streamLike;
}

/**
 * DETERMINISTIC playable check: a Bunny video is playable once it has at
 * least one mp4 variant. Do NOT gate on status === 'ready' — Bunny parks
 * status below READY even at 100% encode (see project memory
 * project_bunny_transcode_status_flag).
 * @param {Object} streamLike - asset, version, or bunnyStream
 * @returns {boolean}
 */
function isBunnyPlayable(streamLike) {
  const stream = resolveStream(streamLike);
  return Array.isArray(stream?.mp4Variants) && stream.mp4Variants.length > 0;
}

/**
 * True if a Bunny video transcode has failed.
 * @param {Object} streamLike - asset, version, or bunnyStream
 * @returns {boolean}
 */
export function isBunnyFailed(streamLike) {
  return resolveStream(streamLike)?.status === BUNNY_STREAM_STATUS.FAILED;
}

/**
 * True if a Bunny video is still transcoding — Bunny-backed, has a videoId,
 * not yet playable, and not failed.
 * @param {Object} assetOrVersion - asset or review version
 * @returns {boolean}
 */
export function isBunnyTranscoding(assetOrVersion) {
  const stream = resolveStream(assetOrVersion);
  if (!stream?.videoId) return false;
  return !isBunnyPlayable(stream) && !isBunnyFailed(stream);
}

/**
 * The crossorigin attribute value for a <video>/<img> serving this asset or
 * version. Bunny's CDN sends no Access-Control-Allow-Origin header, so
 * Bunny media must NOT set crossorigin (else playback/canvas breaks).
 * Decided from pointers, never from the unreliable status string.
 * @param {Object} assetOrVersion - asset or review version
 * @returns {'anonymous'|null}
 */
export function bunnyCrossorigin(assetOrVersion) {
  const isBunny =
    isBunnyBackedAsset(assetOrVersion) || isBunnyVersion(assetOrVersion);
  return isBunny ? null : 'anonymous';
}